import { RefObject, useCallback } from "react";

import BulkActions from "components/BulkActions";
import BulkActionsDrawer from "components/BulkActions/Drawer";
import BulkActionsDrawerActionSteps from "components/BulkActions/Drawer/ActionSteps";
import BulkActionsDrawerActionStepsHeader from "components/BulkActions/Drawer/ActionSteps/Header";
import BulkActionsDrawerActionStepsInfoBanner from "components/BulkActions/Drawer/ActionSteps/InfoBanner";
import BulkActionsDrawerResultsStep from "components/BulkActions/Drawer/ResultsStep";
import BulkActionsDrawerResultsStepBody from "components/BulkActions/Drawer/ResultsStep/Body";
import BulkActionsDrawerResultsStepBodyEmptyTab from "components/BulkActions/Drawer/ResultsStep/Body/EmptyTab";
import BulkActionsDrawerResultsStepFooter from "components/BulkActions/Drawer/ResultsStep/Footer";
import BulkActionsDrawerResultsStepHeader from "components/BulkActions/Drawer/ResultsStep/Header";
import BulkActionsFloatingBar from "components/BulkActions/FloatingBar";
import BulkActionsFloatingBarHeader from "components/BulkActions/FloatingBar/Header";
import BulkActionsFloatingBarSteps from "components/BulkActions/FloatingBar/Steps";
import { type BulkActionItemTriggerResult } from "components/BulkActions/helpers";
import {
  BulkActionItemID,
  BulkActionResultTabs,
  BulkActionsResult,
  BulkActionsStep,
  BulkActionsVariant,
} from "components/BulkActions/types";
import useBulkActions from "components/BulkActions/useBulkActions";
import useExecutionQueue from "components/BulkActions/useExecutionQueue";
import { useResultTabItems } from "components/BulkActions/useResultTabItems";
import BulkActionsPatternsActionsList from "components/BulkActionsPatterns/ActionsList";
import useAnalytics from "hooks/useAnalytics";
import { AnalyticsPageIntegration } from "hooks/useAnalytics/pages/integrations";
import { AiIntegrationView } from "shared/AiIntegration/useSearchAiIntegrations/gql";

import AiIntegrationsBulkActionsConfirmActionForm from "./ConfirmActionForm";
import AiIntegrationsBulkActionsConfirmationView from "./ConfirmationView";
import AiIntegrationsBulkActionsResultItem from "./ResultItem";
import AiIntegrationsBulkActionsSelectedItemsView from "./SelectedItemsView";
import {
  AiIntegrationsBulkActionsAnalyticsPayload,
  AiIntegrationsMutationVariables,
} from "./types";
import useAiIntegrationsBulkActions from "./useAiIntegrationsBulkActions";

type AiIntegrationsBulkActionsProps = {
  virtualizedListContainerRef?: RefObject<HTMLElement | null>;
  selectedSet: Set<BulkActionItemID>;
  integrationsMap: Map<BulkActionItemID, AiIntegrationView>;
  onBulkResetAll: () => void;
  onItemDismiss: (id: BulkActionItemID) => void;
  onFinish: () => Promise<unknown>;
  onHeightChange?: (height: number) => void;
};

const AiIntegrationsBulkActions = ({
  selectedSet,
  integrationsMap,
  onBulkResetAll,
  onItemDismiss,
  virtualizedListContainerRef,
  onFinish,
  onHeightChange,
}: AiIntegrationsBulkActionsProps) => {
  const selectedItemsCount = selectedSet.size;

  const aiIntegrationsBulkActions = useAiIntegrationsBulkActions();
  const trackSegmentAnalyticsEvent = useAnalytics({
    page: AnalyticsPageIntegration.IntegrationsList,
  });

  const {
    selectedBulkAction,
    resetSelectedAction,
    availableActions,
    itemsPerAction,
    actionsPerItem,
    selectedEntityItems,
    applicableSelectedEntityItems,
    skippedSelectedEntityItems,
  } = useBulkActions({
    actions: aiIntegrationsBulkActions,
    selectedSet,
    entityItemsMap: integrationsMap,
  });

  const onItemTrigger = useCallback(
    (
      id: BulkActionItemID,
      mutationVariables: AiIntegrationsMutationVariables
    ): Promise<BulkActionItemTriggerResult> => {
      const integration = integrationsMap.get(id);
      if (!integration || !selectedBulkAction) {
        return Promise.reject();
      }
      if (!selectedBulkAction.condition(integration).result) {
        return Promise.resolve(BulkActionsResult.Skipped);
      }
      return selectedBulkAction.mutation(integration, mutationVariables);
    },
    [integrationsMap, selectedBulkAction]
  );

  const {
    triggerExecution,
    bulkActionItemResults,
    stopExecution,
    stopExecutionForItem,
    bulkActionResultsMetadata,
    resetBulkActionsExecutionQueue,
  } = useExecutionQueue({
    onItemTrigger,
    onAllItemsFinished: onFinish,
    analyticsPage: AnalyticsPageIntegration.IntegrationsList,
  });

  const { resultsTabItems, saveEntityMap, resetEntityMap } =
    useResultTabItems<AiIntegrationView>(bulkActionItemResults);

  const handleClose = useCallback(() => {
    onBulkResetAll();
    resetSelectedAction();
    stopExecution();
    resetBulkActionsExecutionQueue();
    resetEntityMap();
  }, [
    onBulkResetAll,
    resetBulkActionsExecutionQueue,
    resetEntityMap,
    resetSelectedAction,
    stopExecution,
  ]);

  const handleOnConfirm = useCallback(
    (
      mutationVariables: AiIntegrationsMutationVariables,
      analyticsPayload: AiIntegrationsBulkActionsAnalyticsPayload
    ) => {
      if (selectedBulkAction) {
        const items = itemsPerAction.get(selectedBulkAction.key) || [];
        const skippedItems = skippedSelectedEntityItems.map((item) => item.id);
        triggerExecution(items, mutationVariables, skippedItems);
        saveEntityMap(integrationsMap);
        trackSegmentAnalyticsEvent("Bulk Actions - Action Confirmed", {
          applicableCount: items?.length || 0,
          notApplicableCount: skippedItems.length,
          action: selectedBulkAction.key,
          ...analyticsPayload,
        });
      }
    },
    [
      itemsPerAction,
      saveEntityMap,
      selectedBulkAction,
      skippedSelectedEntityItems,
      integrationsMap,
      trackSegmentAnalyticsEvent,
      triggerExecution,
    ]
  );

  const handleOnCancel = (variant: BulkActionsVariant) => () => {
    if (selectedBulkAction) {
      resetSelectedAction();
      const items = itemsPerAction.get(selectedBulkAction.key);
      trackSegmentAnalyticsEvent("Bulk actions - Action Cancelled", {
        applicableCount: items?.length || 0,
        notApplicableCount: skippedSelectedEntityItems.length,
        action: selectedBulkAction.key,
        view: variant,
      });
    }
  };

  const handleContinueWith = useCallback(() => {
    resetSelectedAction();
    resetBulkActionsExecutionQueue();
  }, [resetSelectedAction, resetBulkActionsExecutionQueue]);

  return (
    <BulkActions
      selectedItemsCount={selectedItemsCount}
      onClose={handleClose}
      onContinueWith={handleContinueWith}
      isExecutingBulkActions={!!resultsTabItems[BulkActionResultTabs.Pending].count}
    >
      <BulkActionsFloatingBar
        virtualizedListContainerRef={virtualizedListContainerRef}
        onHeightChange={onHeightChange}
      >
        <BulkActionsFloatingBarHeader
          selectedItemsCount={selectedItemsCount}
          applicableItemsCount={
            selectedBulkAction?.key ? itemsPerAction.get(selectedBulkAction.key)?.length : 0
          }
          analyticsPage={AnalyticsPageIntegration.IntegrationsList}
        />
        <BulkActionsFloatingBarSteps>
          {!selectedBulkAction && (
            <BulkActionsPatternsActionsList
              variant={BulkActionsVariant.FloatingBar}
              availableActions={availableActions}
              onEmptyActionsCancel={handleClose}
            />
          )}

          {selectedBulkAction && (
            <AiIntegrationsBulkActionsConfirmActionForm
              variant={BulkActionsVariant.FloatingBar}
              action={selectedBulkAction}
              onConfirm={handleOnConfirm}
              onCancel={handleOnCancel(BulkActionsVariant.FloatingBar)}
            />
          )}
        </BulkActionsFloatingBarSteps>
      </BulkActionsFloatingBar>

      <BulkActionsDrawer>
        <BulkActionsDrawerActionSteps>
          <BulkActionsDrawerActionStepsHeader
            analyticsPage={AnalyticsPageIntegration.IntegrationsList}
            step={
              !selectedBulkAction
                ? BulkActionsStep.ChooseAction
                : BulkActionsStep.ActionConfirmation
            }
          />
          <BulkActionsDrawerActionStepsInfoBanner />

          {!selectedBulkAction && (
            <AiIntegrationsBulkActionsSelectedItemsView
              items={selectedEntityItems}
              onItemDismiss={onItemDismiss}
              availableActions={availableActions}
              actionsPerItem={actionsPerItem}
              onEmptyActionsCancel={handleClose}
            />
          )}
          {selectedBulkAction && (
            <AiIntegrationsBulkActionsConfirmationView
              applicableItems={applicableSelectedEntityItems}
              skippedItems={skippedSelectedEntityItems}
              onItemDismiss={onItemDismiss}
              selectedBulkAction={selectedBulkAction}
              onConfirm={handleOnConfirm}
              onCancel={handleOnCancel(BulkActionsVariant.Drawer)}
            />
          )}
        </BulkActionsDrawerActionSteps>

        <BulkActionsDrawerResultsStep>
          {(props) => (
            <>
              <BulkActionsDrawerResultsStepHeader
                title={selectedBulkAction?.resultTitle || "Results"}
                analyticsPage={AnalyticsPageIntegration.IntegrationsList}
              />
              <BulkActionsDrawerResultsStepBody {...props} counter={resultsTabItems}>
                {({ currentTab }) =>
                  resultsTabItems[currentTab].count === 0 ? (
                    <BulkActionsDrawerResultsStepBodyEmptyTab currentTab={currentTab} />
                  ) : (
                    resultsTabItems[currentTab].entitiesList.map(({ status, items }) =>
                      items.map((integration) => (
                        <AiIntegrationsBulkActionsResultItem
                          key={integration.id}
                          integration={integration}
                          resultsMetadata={bulkActionResultsMetadata}
                          stopExecution={() => stopExecutionForItem(integration.id)}
                          status={status}
                        />
                      ))
                    )
                  )
                }
              </BulkActionsDrawerResultsStepBody>
              <BulkActionsDrawerResultsStepFooter
                {...props}
                onContinueEnabled
                bulkActionItemResults={bulkActionItemResults}
                stopExecution={stopExecution}
                analyticsPage={AnalyticsPageIntegration.IntegrationsList}
              />
            </>
          )}
        </BulkActionsDrawerResultsStep>
      </BulkActionsDrawer>
    </BulkActions>
  );
};

export default AiIntegrationsBulkActions;
